import { Immutable } from "immer";
import React from "react";

import {
    IOptionCode,
    IOptionSingleValue,
    IProduct,
} from "../../models/catalogue";
import { PriceType, getProductPrice } from "../../utils/catalogue";
import { roundedMoney } from "../../utils/format";
import { SelectOptions } from "../SelectOptions";

import styles from "./index.module.scss";

type IProps = Immutable<{
    selectorID: string;
    code: IOptionCode;
    value?: IOptionSingleValue;
    options: IOptionSingleValue[];
    prefixNameCapped: string;
    configuratorClass: string;
    prefixClass: string;
    selectClass: string;
    variant?: IProduct | null;
    showPricing?: boolean;
    onChange: (event: React.FormEvent<HTMLSelectElement>) => void;
}>;

const buildPricingOverlay = (props: IProps): React.ReactNode => {
    if (
        !props.showPricing ||
        !props.variant ||
        !props.variant.availability.is_available_to_buy
    ) {
        return null;
    }
    const retailPrice = getProductPrice(props.variant.price, {
        priceType: PriceType.RETAIL,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    const cosmeticPrice = getProductPrice(props.variant.price, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    return (
        <div className={styles.pricingOverlay}>
            <span className={styles.actualPrice}>
                <span className="ada-screenreader-only">Original price: </span>
                {roundedMoney(cosmeticPrice)}
            </span>
            <span className={styles.retailPrice}>
                <span className="ada-screenreader-only">Current price: </span>
                {roundedMoney(retailPrice)}
            </span>
        </div>
    );
};

export const ProductOptionDropdownSelector = (
    props: IProps,
): React.ReactNode => {
    const value = props.value ?? "";
    if (props.options.length === 1) {
        return (
            <div key={props.code} className="configurator__option-container">
                <label className={props.prefixClass}>
                    {props.prefixNameCapped}
                </label>
                <div className={props.configuratorClass}>
                    {value}
                    {buildPricingOverlay(props)}
                </div>
            </div>
        );
    }
    return (
        <div
            key={props.selectorID}
            className={`${styles.root} configurator__option-container`}
        >
            <label htmlFor={props.selectorID} className={props.prefixClass}>
                {props.prefixNameCapped}
            </label>
            <div className={props.configuratorClass}>
                <select
                    value={value}
                    name={props.code}
                    onChange={props.onChange}
                    className={props.selectClass}
                    id={props.selectorID}
                >
                    <optgroup label={props.prefixNameCapped}>
                        <SelectOptions options={props.options} />
                    </optgroup>
                </select>
                {buildPricingOverlay(props)}
            </div>
        </div>
    );
};
